<script setup lang="ts">
  import { computed, ref, watch } from 'vue';
  import { useEventListener, useResizeObserver } from '@vueuse/core';
  import PosUiEmptyState from '@/components/pos-v2/ui/PosUiEmptyState.vue';
  import {
    IV2StatsTableColumn,
    IV2StatsTableRow,
  } from './v2-stats-table.types';

  const props = withDefaults(
    defineProps<{
      columns: IV2StatsTableColumn[];
      rows: IV2StatsTableRow[];
      /** Pinned totals row rendered on top (raw values, same keys). */
      totals?: Record<string, unknown> | null;
      loading?: boolean;
      defaultSort?: { key: string; desc?: boolean } | null;
      /** Emits 'row-click' for leaf rows and makes them hoverable. */
      clickableRows?: boolean;
      /** Keeps the first column visible while scrolling horizontally. */
      stickyFirst?: boolean;
      emptyText?: string;
    }>(),
    {
      totals: null,
      loading: false,
      defaultSort: null,
      clickableRows: false,
      stickyFirst: false,
      emptyText: undefined,
    }
  );

  const emit = defineEmits<{
    (e: 'row-click', row: IV2StatsTableRow): void;
  }>();

  const sortKey = ref<string | null>(props.defaultSort?.key ?? null);
  const sortDesc = ref(props.defaultSort?.desc ?? true);
  const expandedIds = ref<Set<string | number>>(new Set());

  // Reset drill state when the dataset changes (new range/filter).
  watch(
    () => props.rows,
    () => {
      expandedIds.value = new Set();
    }
  );

  function onHeaderClick(col: IV2StatsTableColumn) {
    if (col.sortable === false) return;
    if (sortKey.value === col.key) {
      sortDesc.value = !sortDesc.value;
    } else {
      sortKey.value = col.key;
      sortDesc.value = !!col.numeric;
    }
  }

  function compareRows(
    a: IV2StatsTableRow,
    b: IV2StatsTableRow,
    key: string,
    desc: boolean
  ): number {
    const av = a[key];
    const bv = b[key];
    let result: number;
    if (av === null || typeof av === 'undefined') {
      result = bv === null || typeof bv === 'undefined' ? 0 : 1;
    } else if (bv === null || typeof bv === 'undefined') {
      result = -1;
    } else if (typeof av === 'number' && typeof bv === 'number') {
      result = av - bv;
    } else {
      result = String(av).localeCompare(String(bv));
    }
    return desc ? -result : result;
  }

  function sortLevel(rows: IV2StatsTableRow[]): IV2StatsTableRow[] {
    const key = sortKey.value;
    if (!key) return rows;
    const desc = sortDesc.value;
    return [...rows].sort((a, b) => compareRows(a, b, key, desc));
  }

  /**
   * Flattened visible rows: sorted parents with sorted expanded children,
   * recursively (drill trees can be several levels deep). Callers must give
   * every row a globally unique id — expansion state is keyed by it.
   */
  const visibleRows = computed(() => {
    const flat: Array<{ row: IV2StatsTableRow; depth: number }> = [];
    const pushLevel = (rows: IV2StatsTableRow[], depth: number) => {
      for (const row of sortLevel(rows)) {
        flat.push({ row, depth });
        if (row.children?.length && expandedIds.value.has(row.id)) {
          pushLevel(row.children, depth + 1);
        }
      }
    };
    pushLevel(props.rows, 0);
    return flat;
  });

  function isExpandable(row: IV2StatsTableRow): boolean {
    return !!row.children?.length;
  }

  function toggleExpand(row: IV2StatsTableRow) {
    const next = new Set(expandedIds.value);
    if (next.has(row.id)) next.delete(row.id);
    else next.add(row.id);
    expandedIds.value = next;
  }

  function onRowClick(row: IV2StatsTableRow) {
    if (isExpandable(row)) {
      toggleExpand(row);
      return;
    }
    if (props.clickableRows) emit('row-click', row);
  }

  function formatCell(col: IV2StatsTableColumn, row: IV2StatsTableRow): string {
    const value = row[col.key];
    if (col.format) return col.format(value, row);
    if (value === null || typeof value === 'undefined') return '';
    return String(value);
  }

  const totalsRow = computed<IV2StatsTableRow | null>(() =>
    props.totals ? { id: '__totals__', ...props.totals } : null
  );

  //#region Horizontal scroll affordances

  const scrollRef = ref<HTMLElement | null>(null);
  const tableRef = ref<HTMLTableElement | null>(null);

  /** Scrolled right of origin: rows now slide under the pinned column. */
  const scrolledX = ref(false);
  /** More columns remain off-screen to the right. */
  const canScrollRight = ref(false);

  function measureScrollEdges() {
    const el = scrollRef.value;
    if (!el) return;
    scrolledX.value = el.scrollLeft > 0;
    canScrollRight.value = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
  }

  useEventListener(scrollRef, 'scroll', measureScrollEdges, { passive: true });
  // Content growth (data/columns loading in) fires no scroll event, so
  // re-measure whenever the container or the table itself resizes.
  useResizeObserver(scrollRef, measureScrollEdges);
  useResizeObserver(tableRef, measureScrollEdges);

  //#endregion
</script>

<template>
  <div
    class="v2-stats-table"
    :class="{ 'sticky-first': stickyFirst, 'is-scrolled-x': scrolledX }"
  >
    <div ref="scrollRef" class="table-scroll">
      <table ref="tableRef" class="table-el">
        <thead>
          <tr>
            <th
              v-for="col in columns"
              :key="col.key"
              class="table-header-cell"
              :class="{
                'is-numeric': col.numeric,
                'is-sortable': col.sortable !== false,
              }"
              @click="onHeaderClick(col)"
            >
              <span class="header-label">{{ col.label }}</span>
              <i
                v-if="sortKey === col.key"
                class="header-sort-icon mdi"
                :class="sortDesc ? 'mdi-arrow-down' : 'mdi-arrow-up'"
              />
            </th>
          </tr>
        </thead>

        <tbody v-if="loading">
          <tr v-for="n in 6" :key="n" class="table-skeleton-row">
            <td v-for="col in columns" :key="col.key" class="table-cell">
              <div class="cell-skeleton" />
            </td>
          </tr>
        </tbody>

        <tbody v-else-if="visibleRows.length">
          <tr v-if="totalsRow" class="table-totals-row">
            <td
              v-for="(col, colIdx) in columns"
              :key="col.key"
              class="table-cell"
              :class="{ 'is-numeric': col.numeric }"
            >
              <template v-if="colIdx === 0 && totalsRow[col.key] == null">
                {{ $t('v2Stats.table.total') }}
              </template>
              <template v-else>{{ formatCell(col, totalsRow) }}</template>
            </td>
          </tr>

          <!-- Depth-qualified keys: API drill children may reuse the parent's
               id (e.g. a category's "self" row). -->
          <tr
            v-for="entry in visibleRows"
            :key="entry.depth + ':' + entry.row.id"
            class="table-row"
            :class="{
              'is-child': entry.depth > 0,
              'is-clickable': isExpandable(entry.row) || clickableRows,
            }"
            @click="onRowClick(entry.row)"
          >
            <td
              v-for="(col, colIdx) in columns"
              :key="col.key"
              class="table-cell"
              :class="{ 'is-numeric': col.numeric }"
            >
              <div
                v-if="colIdx === 0"
                class="first-cell"
                :style="
                  entry.depth > 0
                    ? { paddingLeft: entry.depth * 1.5 + 'rem' }
                    : undefined
                "
              >
                <i
                  v-if="entry.row.children?.length"
                  class="expand-icon mdi mdi-chevron-right"
                  :class="{ 'is-expanded': expandedIds.has(entry.row.id) }"
                />
                <slot
                  :name="'cell-' + col.key"
                  :row="entry.row"
                  :depth="entry.depth"
                >
                  <span class="first-cell-text">{{
                    formatCell(col, entry.row)
                  }}</span>
                </slot>
              </div>
              <slot
                v-else
                :name="'cell-' + col.key"
                :row="entry.row"
                :depth="entry.depth"
              >
                {{ formatCell(col, entry.row) }}
              </slot>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="scroll-more-fade" :class="{ 'is-visible': canScrollRight }" />

    <PosUiEmptyState
      v-if="!loading && !visibleRows.length"
      icon="table-off"
      :title="emptyText || String($t('v2Stats.table.empty'))"
    />
  </div>
</template>

<style scoped lang="scss">
  .v2-stats-table {
    @apply tw-relative tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-white;

    .table-scroll {
      @apply tw-overflow-x-auto;
    }

    .scroll-more-fade {
      @apply tw-pointer-events-none tw-absolute tw-inset-y-0 tw-right-0 tw-z-20 tw-w-8 tw-opacity-0 tw-transition-opacity;
      background: linear-gradient(
        to left,
        rgba(255, 255, 255, 0.95),
        rgba(255, 255, 255, 0)
      );

      &.is-visible {
        @apply tw-opacity-100;
      }
    }

    .table-el {
      @apply tw-w-full tw-border-collapse tw-text-sm;

      .table-header-cell {
        @apply tw-select-none tw-whitespace-nowrap tw-border-0 tw-border-b tw-border-solid tw-border-neutral-200 tw-bg-neutral-50 tw-px-3 tw-py-2.5 tw-text-left tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-neutral-500;

        &.is-numeric {
          @apply tw-text-right;
        }

        &.is-sortable {
          @apply tw-cursor-pointer hover:tw-text-neutral-700;
        }

        .header-sort-icon {
          @apply tw-ml-1 tw-text-sm tw-text-neutral-500;
        }
      }

      .table-cell {
        @apply tw-whitespace-nowrap tw-border-0 tw-border-b tw-border-solid tw-border-neutral-100 tw-px-3 tw-py-2.5 tw-text-left tw-text-neutral-700;

        &.is-numeric {
          @apply tw-text-right tw-font-medium tw-tabular-nums tw-text-neutral-900;
        }

        .cell-skeleton {
          @apply tw-h-4 tw-w-16 tw-animate-pulse tw-rounded tw-bg-neutral-200;
        }

        .first-cell {
          @apply tw-flex tw-items-center;

          .expand-icon {
            @apply tw-mr-1 tw-shrink-0 tw-text-base tw-text-neutral-400 tw-transition-transform;

            &.is-expanded {
              @apply tw-rotate-90;
            }
          }
        }
      }

      .table-totals-row {
        .table-cell {
          @apply tw-border-b tw-border-solid tw-border-neutral-200 tw-bg-neutral-50 tw-font-semibold tw-text-neutral-900;
        }
      }

      .table-row {
        &.is-clickable {
          @apply tw-cursor-pointer hover:tw-bg-neutral-50;
        }

        &.is-child {
          .table-cell {
            @apply tw-bg-neutral-50/60 tw-text-neutral-600;
          }
        }

        &:last-child {
          .table-cell {
            @apply tw-border-b-0;
          }
        }
      }

      // Phones: cap the label column so the metric columns keep most of the
      // viewport (a long product name would otherwise pin ~80% of it), and
      // tighten cell padding for density.
      @media (max-width: 767.98px) {
        .table-header-cell,
        .table-cell {
          @apply tw-px-2;
        }

        // min-width floor: label text is the table's only compressible
        // content (numeric cells are nowrap), so without it the auto layout
        // squeezes this column to almost nothing when columns overflow.
        .table-header-cell:first-child {
          @apply tw-overflow-hidden tw-text-ellipsis;
          min-width: 9rem;
          max-width: 45vw;
        }

        .table-cell:first-child {
          @apply tw-whitespace-normal;
          min-width: 9rem;
          max-width: 45vw;

          .first-cell {
            @apply tw-flex-wrap;

            // flex-1 (basis 0): with flex-wrap, an auto basis equals the
            // full text width, wrapping the span below the expand icon.
            .first-cell-text {
              @apply tw-min-w-0 tw-flex-1 tw-overflow-hidden;
              display: -webkit-box;
              -webkit-box-orient: vertical;
              -webkit-line-clamp: 2;
              word-break: break-word; // fallback for Chrome <80
              overflow-wrap: anywhere;
            }
          }
        }
      }
    }

    &.sticky-first {
      .table-el {
        .table-header-cell:first-child,
        .table-cell:first-child {
          @apply tw-sticky tw-left-0 tw-z-10 tw-bg-white;
        }

        .table-header-cell:first-child {
          @apply tw-bg-neutral-50;
        }

        .table-totals-row .table-cell:first-child {
          @apply tw-bg-neutral-50;
        }

        // Pinned cells need opaque backgrounds (scrolled content shows
        // through translucent ones), so restate row tints solidly here.
        .table-row.is-child {
          .table-cell:first-child {
            @apply tw-bg-neutral-50;
          }
        }

        .table-row.is-clickable:hover {
          .table-cell:first-child {
            @apply tw-bg-neutral-50;
          }
        }
      }

      // Once scrolled, rows slide under the pinned column: separate it
      // with a soft right-edge shadow.
      &.is-scrolled-x {
        .table-el {
          .table-header-cell:first-child,
          .table-cell:first-child {
            box-shadow: 6px 0 8px -6px rgba(0, 0, 0, 0.18);
          }
        }
      }
    }
  }
</style>
